import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SimplePageEvent } from '../../types/paginator.types';

@Component({
    selector: 'paginator',
    templateUrl: './paginator.component.html',
    styleUrls: ['./paginator.component.scss'],
    standalone: false
})
export class PaginatorComponent implements OnInit, OnChanges {
  @Input() currentPage = 1; // 1-based for UI
  @Input() totalItems = 0;
  @Input() limit = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];

  @Output() pageChange = new EventEmitter<SimplePageEvent>();

  pages: number[] = [];
  totalPages = 1;

  /* ---------- Lifecycle ---------- */

  ngOnInit(): void {
    this.recalculate();
    this.emitPageChange(); // one guaranteed initial emit
  }

  ngOnChanges(_: SimpleChanges): void {
    // Recalculate only; do NOT emit
    this.recalculate();
  }

  /* ---------- Core logic ---------- */

  private recalculate(): void {
    this.totalPages = Math.max(Math.ceil(this.totalItems / this.limit), 1);

    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  private emitPageChange(): void {
    this.pageChange.emit({
      pageIndex: this.currentPage - 1, // 0-based (MatPaginator-compatible)
      pageSize: this.limit,
      length: this.totalItems,
    });
  }

  /* ---------- Navigation ---------- */

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.emitPageChange();
    }
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  /* ---------- Page size ---------- */

  onPageSizeChange(size: number | string): void {
    this.limit = +size;
    this.currentPage = 1;
    this.recalculate();
    this.emitPageChange();
  }

  get rangeStart(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.limit + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.limit, this.totalItems);
  }
}
